/**
 * PipelineExportMenu — Pursuit Pipeline export dropdown.
 *
 * Mounts next to the existing filter row. Streams PDF / XLSX / CSV
 * from the backend honoring the currently applied filters. Triggers
 * a browser download with a filename like
 *   fedbd-pipeline_<tenant>_<yyyyMMddHHmm>.<ext>
 *
 * Requirement #9 — v3.16.0.
 */
import React, { useState } from 'react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Download,
  FileSpreadsheet,
  FileText,
  FileCode2,
  Loader2,
  History,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Read the file from the streamed response and trigger a browser download.
 */
async function streamDownload(res) {
  const blob = await res.blob();
  const dispo = res.headers.get('content-disposition') || '';
  const m = dispo.match(/filename="?([^";]+)"?/i);
  const filename = m ? m[1] : `pipeline-export-${Date.now()}`;
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

export default function PipelineExportMenu({
  filters = {},
  disabled = false,
  rowCount = 0,
  onShowHistory,
}) {
  const { getAuthHeaders } = useAuth();
  const [downloading, setDownloading] = useState(null); // 'pdf' | 'xlsx' | 'csv' | null

  const handleExport = async (format) => {
    if (downloading) return;
    setDownloading(format);
    try {
      const params = new URLSearchParams();
      params.set('format', format);
      Object.entries(filters).forEach(([k, v]) => {
        if (v !== null && v !== undefined && v !== '' && v !== 'all') {
          params.set(k, String(v));
        }
      });
      const res = await fetch(
        `${API_URL}/api/exports/pursuits?${params.toString()}`,
        { headers: getAuthHeaders() },
      );
      if (res.status === 413) {
        const body = await res.json();
        toast.error(
          body?.detail?.message ||
            'Export too large — narrow the filters and try again.',
        );
        return;
      }
      if (!res.ok) {
        throw new Error(`HTTP ${res.status}`);
      }
      await streamDownload(res);
      const rows = res.headers.get('x-export-row-count') || rowCount;
      toast.success(`Exported ${rows} pursuit${rows === 1 ? '' : 's'} as ${format.toUpperCase()}.`);
    } catch (e) {
      console.error('Pipeline export failed', e);
      toast.error('Export failed. Please try again.');
    } finally {
      setDownloading(null);
    }
  };

  const isDisabled = disabled || rowCount === 0 || downloading !== null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="outline"
          className="text-teal-700 border-teal-300 hover:bg-teal-50"
          disabled={isDisabled}
          data-testid="pipeline-export-menu"
        >
          {downloading ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Download className="w-4 h-4 mr-2" />
          )}
          Export
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-56">
        <DropdownMenuLabel className="text-xs text-slate-500">
          Export current view ({rowCount.toLocaleString()})
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={() => handleExport('pdf')}
          disabled={downloading !== null}
          data-testid="pipeline-export-pdf"
        >
          <FileText className="w-4 h-4 mr-2 text-red-500" />
          Export as PDF
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => handleExport('xlsx')}
          disabled={downloading !== null}
          data-testid="pipeline-export-xlsx"
        >
          <FileSpreadsheet className="w-4 h-4 mr-2 text-emerald-600" />
          Export as XLSX
        </DropdownMenuItem>
        <DropdownMenuItem
          onClick={() => handleExport('csv')}
          disabled={downloading !== null}
          data-testid="pipeline-export-csv"
        >
          <FileCode2 className="w-4 h-4 mr-2 text-slate-600" />
          Export as CSV
        </DropdownMenuItem>
        {onShowHistory && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onClick={onShowHistory}
              data-testid="pipeline-export-history-btn"
            >
              <History className="w-4 h-4 mr-2 text-slate-500" />
              Recent exports…
            </DropdownMenuItem>
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
